import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetches the live pricing catalog from the backend's
 * `/api/pricing/options/search` endpoint. Replaces the need to lean on the
 * legacy `mockData.js` hard-coded taxonomy for price discovery.
 *
 * Returns `{ loading, error, options, categories, formats, finishes, refetch }`.
 * The `categories/formats/finishes` arrays are automatically narrowed by
 * whatever filters are currently applied, so callers can build cascading
 * dropdowns without any client-side joins.
 */
export function usePricingCatalog({ category, format, finish, q } = {}) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    options: [],
    categories: [],
    formats: [],
    finishes: [],
    count: 0,
  });

  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const params = {};
      if (category) params.category = category;
      if (format) params.format = format;
      if (finish) params.finish = finish;
      if (q) params.q = q;
      const { data } = await axios.get(`${API_URL}/api/pricing/options/search`, { params });
      setState({
        loading: false,
        error: null,
        options: data.options || [],
        categories: data.categories || [],
        formats: data.formats || [],
        finishes: data.finishes || [],
        count: data.count || 0,
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err?.message || 'Failed to load catalog' }));
    }
  }, [category, format, finish, q]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...state, refetch };
}

/**
 * Computes a live price for a single catalog combo + dimensions + quantity.
 * Honors the caller's auth session (so Evergreen-tier users see their 30% off).
 */
export async function calculatePrice({ category, format, finish, width, height, quantity, tier }) {
  const { data } = await axios.post(`${API_URL}/api/pricing/calculate`, {
    category,
    format,
    finish,
    width,
    height,
    quantity,
    ...(tier ? { tier } : {}),
  });
  return data;
}
